import React, { useState } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  DollarSign,
  AlertTriangle,
  Clock,
  FileText,
  TrendingUp,
  MessageCircle,
  CheckCircle,
  Edit,
  Save,
  ChevronDown,
  Flag,
  Star,
  Hash
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';
import { toast } from 'sonner';
import {
  OverdueGroup,
  OverdueTransaction,
  OverdueStatus,
  OverduePriority,
  RiskLevel,
  OVERDUE_STATUS_COLORS,
  OVERDUE_PRIORITY_COLORS,
  OVERDUE_STATUS_LABELS,
  OVERDUE_PRIORITY_LABELS,
  formatOverdueDays
} from '../../types/overdue';

interface TransactionDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: OverdueGroup | null;
  onPriorityChange?: (transactionId: string, priority: OverduePriority) => void;
  onStatusChange?: (transactionId: string, status: OverdueStatus) => void;
  onSendNotice?: (clientId: string, method: 'email' | 'phone' | 'whatsapp', transactionIds?: string[]) => void;
  onAddNotes?: (transactionId: string, notes: string) => void;
  onMarkResolved?: (transactionId: string) => void;
  onScheduleFollowUp?: (transactionId: string, date: Date) => void;
}

export function TransactionDetailsModal({
  open,
  onOpenChange,
  group,
  onPriorityChange,
  onStatusChange,
  onSendNotice,
  onAddNotes,
  onMarkResolved,
  onScheduleFollowUp,
}: TransactionDetailsModalProps) {
  const [selectedTransaction, setSelectedTransaction] = useState<OverdueTransaction | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [editingNotes, setEditingNotes] = useState<string>('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<string>('');

  React.useEffect(() => {
    if (group && group.transactions.length > 0) {
      setSelectedTransaction(group.transactions[0]);
      setEditingNotes(group.transactions[0].notes || '');
    }
  }, [group]);

  if (!group) return null;

  const currentTransaction = selectedTransaction || group.transactions[0];

  const handleSaveNotes = async () => {
    if (!onAddNotes || !currentTransaction) return;

    setIsSavingNotes(true);
    try {
      await onAddNotes(currentTransaction.id, editingNotes);
      toast.success('Notas salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar notas!');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handlePriorityChange = (priority: OverduePriority) => {
    if (onPriorityChange && currentTransaction) {
      onPriorityChange(currentTransaction.id, priority);
      toast.success('Prioridade atualizada com sucesso!');
    }
  };

  const handleStatusChange = (status: OverdueStatus) => {
    if (onStatusChange && currentTransaction) {
      onStatusChange(currentTransaction.id, status);
      toast.success('Status atualizado com sucesso!');
    }
  };

  const handleSendNotice = (method: 'email' | 'phone' | 'whatsapp') => {
    if (onSendNotice && group.client) {
      onSendNotice(group.client.id, method, [currentTransaction.id]);
      toast.success(`Notificação via ${method} enviada com sucesso!`);
    }
  };

  const handleMarkResolved = () => {
    if (onMarkResolved && currentTransaction) {
      onMarkResolved(currentTransaction.id);
      toast.success('Transação marcada como resolvida!');
      onOpenChange(false);
    }
  };

  const handleScheduleFollowUp = () => {
    if (onScheduleFollowUp && currentTransaction && followUpDate) {
      onScheduleFollowUp(currentTransaction.id, new Date(followUpDate));
      toast.success('Follow-up agendado com sucesso!');
      setFollowUpDate('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Detalhes da Transação
          </DialogTitle>
          <DialogDescription>
            Visualize e gerencie informações detalhadas da transação atrasada
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Detalhes</TabsTrigger>
            <TabsTrigger value="client">Cliente</TabsTrigger>
            <TabsTrigger value="actions">Ações</TabsTrigger>
            <TabsTrigger value="history">Histórico</TabsTrigger>
          </TabsList>

          <ScrollArea className="max-h-[60vh] pr-4">
            <TabsContent value="details" className="space-y-4">
              {/* Transaction Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Informações da Transação
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Descrição</Label>
                      <p className="font-medium">{currentTransaction.description}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Referência</Label>
                      <p className="font-medium">{currentTransaction.contract_reference || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Valor Original</Label>
                      <p className="font-medium">{formatCurrency(currentTransaction.original_amount)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Valor Atual</Label>
                      <p className="font-medium text-finance-red">
                        {formatCurrency(currentTransaction.current_amount)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Data de Vencimento</Label>
                      <p className="font-medium">
                        {currentTransaction.due_date.toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Dias em Atraso</Label>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-sm">
                          {formatOverdueDays(currentTransaction.days_overdue)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                      <div className="mt-1">
                        <Badge
                          variant="outline"
                          style={{
                            borderColor: OVERDUE_STATUS_COLORS[currentTransaction.status],
                            color: OVERDUE_STATUS_COLORS[currentTransaction.status],
                            backgroundColor: `${OVERDUE_STATUS_COLORS[currentTransaction.status]}20`,
                          }}
                        >
                          {OVERDUE_STATUS_LABELS[currentTransaction.status]}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Prioridade</Label>
                      <div className="mt-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="justify-start"
                            >
                              <Flag className="h-4 w-4 mr-2" />
                              {OVERDUE_PRIORITY_LABELS[currentTransaction.priority]}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {Object.values(OverduePriority).map((priority) => (
                              <DropdownMenuItem
                                key={priority}
                                onClick={() => handlePriorityChange(priority)}
                              >
                                <Flag className="h-4 w-4 mr-2" />
                                {OVERDUE_PRIORITY_LABELS[priority]}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Notas</Label>
                    <div className="mt-1 space-y-2">
                      <Textarea
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Adicione notas sobre esta transação..."
                        className="min-h-[100px]"
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleSaveNotes}
                          disabled={isSavingNotes}
                          size="sm"
                        >
                          {isSavingNotes ? (
                            <>
                              <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                              Salvando...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Salvar Notas
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="client" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Informações do Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Nome</Label>
                      <p className="font-medium">{group.client.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Documento</Label>
                      <p className="font-medium">{group.client.document || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{group.client.email || '-'}</p>
                        {group.client.email && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendNotice('email')}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Telefone</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{group.client.phone || '-'}</p>
                        {group.client.phone && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendNotice('phone')}
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendNotice('whatsapp')}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Resumo da Dívida</Label>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Total em Aberto</p>
                        <p className="text-xl font-bold text-finance-red">
                          {formatCurrency(group.client.total_debt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Número de Transações</p>
                        <p className="text-xl font-bold">{group.client.total_transactions}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="actions" className="space-y-4">
              <div className="grid gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ações Rápidas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      onClick={() => handleSendNotice('email')}
                      className="w-full justify-start"
                      variant="outline"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Enviar Cobrança por Email
                    </Button>

                    <Button
                      onClick={() => handleSendNotice('whatsapp')}
                      className="w-full justify-start"
                      variant="outline"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Enviar Cobrança por WhatsApp
                    </Button>

                    <Button
                      onClick={handleMarkResolved}
                      className="w-full justify-start"
                      variant="outline"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Marcar como Resolvido
                    </Button>

                    <div className="space-y-2">
                      <Label>Agendar Follow-up</Label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={followUpDate}
                          onChange={(e) => setFollowUpDate(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleScheduleFollowUp}
                          disabled={!followUpDate}
                          size="sm"
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Agendar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Histórico de Interações
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum histórico de interações registrado</p>
                    <p className="text-sm">As ações realizadas serão registradas aqui</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Hash className="h-4 w-4" />
            ID: {currentTransaction.id}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button onClick={() => setActiveTab('actions')}>
              <MessageCircle className="h-4 w-4 mr-2" />
              Ações de Cobrança
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}